import { useRef, useState } from 'react';
import { isValidPhoneNumber } from 'react-phone-number-input';
import { useImmer } from 'use-immer';
import moment from 'moment';
import validator from 'validator';
import {
  AddAPhoto as AddAPhotoIcon,
  Collections as CollectionsIcon,
  DocumentScanner as DocumentScannerIcon,
  InsertDriveFile as InsertDriveFileIcon,
} from '@mui/icons-material';
import {
  BelongsToSelects,
  Button,
  enqueueSnackbar,
  fullScreenButtonClick,
  ImageGallery,
  ImageGalleryCard,
  ImageGalleryCardWrapper,
  Input,
  InputContainer,
  InputPhoneNumber,
  SearchableSelect,
  Select,
  FileRow,
  Typography,
} from '../../design-system';
import { DashbaordFormFooter, DashboardInputs, DashboardWrapper, HideInput } from '../dashboard';
import {
  containerValidator,
  extendContainerValidator,
  filesFormatValidator,
  getPhoneNumberValue,
  downloadBlob,
} from '../../utils';
import {
  useGetContainerOcrMutation,
  useAddGateInMutation,
  useCompleteGateInMutation,
  useUpdateImagesMutation,
  useUpdateDocumentsMutation,
} from '../../hooks';
import { controlBackdropLoading, gateInsService } from '../../services';
import {
  containerConditionOptions,
  containerContents,
  containerTypes,
  INPUT_ERRORS,
  MESSAGES,
  REGEX,
} from '../../const';

export const GateInsAddWithImage = () => {
  const { mutate: getContainerOcr, isLoading: isOcrLoading } = useGetContainerOcrMutation();
  const updateDocumentsMutation = useUpdateDocumentsMutation();
  const updateImagesMutation = useUpdateImagesMutation();
  const completeMutation = useCompleteGateInMutation();
  const gateinAddMutation = useAddGateInMutation();

  const containerId = useRef('');
  const [containerNumber, setContainerNumber] = useState('');
  const [containerType, setContainerType] = useState('');
  const [condition, setCondition] = useState('');
  const [containerContent, setContainerContent] = useState('empty');
  const yardCompanyId = useRef();
  const yardId = useRef();
  const [clientName, setClientName] = useState();
  const clientId = useRef();
  const [licensePlateNumber, setLicensePlateNumber] = useState('');

  const [driverFirstName, setDriverFirstName] = useState('');
  const [driverLastName, setDriverLastName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState<any>();

  const [zoomImageIndex, setZoomImageIndex] = useState(0);
  const [images, setImages] = useImmer<any[]>([]);
  const [imagesFiles, setImagesFiles] = useImmer<any[]>([]);
  const ocrImageInputRef = useRef<any>();
  const ocrCarImageInputRef = useRef<any>();
  const imagesInputRef = useRef<any>();
  const imagesGalleryInputRef = useRef<any>();

  const [files, setFiles] = useImmer<any[]>([]);
  const filesInputRef = useRef<any>();
  const filesGalleryInputRef = useRef<any>();

  const [isSubmitClicked, setIsSubmitClicked] = useState(false);
  const [updateKey, setUpdateKey] = useState(Math.random());

  const validators = {
    containerNumber: !String(containerNumber).length
      ? INPUT_ERRORS.REQUIRED.CONTAINER_NUMBER
      : !containerValidator(containerNumber)
      ? INPUT_ERRORS.NOT_VALID.CONTAINER_NUMBER
      : '',
    containerType: !String(containerType).length ? INPUT_ERRORS.REQUIRED.CONTAINER_TYPE : '',
    containerContent: !String(containerContent).length
      ? INPUT_ERRORS.REQUIRED.CONTAINER_CONTENT
      : '',
    clientName: !String(clientName).length ? INPUT_ERRORS.REQUIRED.CLIENT_NAME : '',
    licensePlateNumber: !String(licensePlateNumber).length
      ? INPUT_ERRORS.REQUIRED.LICENSE_PLATE_NUMBER
      : !validator.isLength(licensePlateNumber, { min: 4, max: 10 })
      ? INPUT_ERRORS.NOT_VALID.LICENSE_PLATE_NUMBER_LENGTH
      : !REGEX.LICENSE_PLATE_NUMBER.test(licensePlateNumber)
      ? INPUT_ERRORS.NOT_VALID.LICENSE_PLATE_NUMBER
      : '',
    images: !images.length
      ? INPUT_ERRORS.REQUIRED.IMAGES
      : images.length > 10
      ? INPUT_ERRORS.NOT_VALID.MAX_UPLOAD_FILES
      : '',
    files: files.length > 10 ? INPUT_ERRORS.NOT_VALID.MAX_UPLOAD_FILES : '',
    driverFirstName:
      !!driverFirstName &&
      (!validator.isLength(driverFirstName, { max: 30 }) || !REGEX.USER_NAME.test(driverFirstName))
        ? INPUT_ERRORS.NOT_VALID.DRIVER_NAME
        : '',
    driverLastName:
      !!driverLastName &&
      (!validator.isLength(driverLastName, { max: 30 }) || !REGEX.USER_NAME.test(driverLastName))
        ? INPUT_ERRORS.NOT_VALID.DRIVER_NAME
        : '',
    phoneNumber:
      !!phoneNumber && !isValidPhoneNumber(`+${getPhoneNumberValue(phoneNumber)}`)
        ? INPUT_ERRORS.NOT_VALID.PHONE_NUMBER
        : '',
  };

  const resetForm = () => {
    containerId.current = '';
    setContainerNumber('');
    setContainerType('');
    setCondition('');
    setContainerContent('');
    yardCompanyId.current = undefined;
    yardId.current = undefined;
    setClientName(undefined);
    clientId.current = undefined;
    setLicensePlateNumber('');

    setDriverFirstName('');
    setDriverLastName('');
    setPhoneNumber(undefined);

    setZoomImageIndex(0);
    setImages([]);
    setImagesFiles([]);

    setFiles([]);

    filesInputRef.current.type = 'text';
    filesInputRef.current.type = 'file';
    filesGalleryInputRef.current.type = 'text';
    filesGalleryInputRef.current.type = 'file';
    imagesInputRef.current.type = 'text';
    imagesInputRef.current.type = 'file';
    imagesGalleryInputRef.current.type = 'text';
    imagesGalleryInputRef.current.type = 'file';

    setIsSubmitClicked(false);
    setUpdateKey(Math.random());
  };

  const handleContainerSelect = (option: any) => {
    if (typeof option === 'string') {
      resetForm();

      setContainerNumber(option);
      return;
    }
    if (option.isWrong) return;

    if (containerId.current === option.id) {
      resetForm();
    } else {
      containerId.current = option.id;
      setContainerNumber(option.value || option.number);

      setContainerType(option.type);
      setCondition(option.condition || '');
      setContainerContent(option.isLoaded ? 'loaded' : 'empty');
      yardCompanyId.current = undefined;
      yardId.current = undefined;
      setClientName(option.clientYard.client.code);
      clientId.current = undefined;

      setLicensePlateNumber(option.gateIn.driver?.licensePlateNumber || '');

      setDriverFirstName(option.gateIn.driver?.firstName || '');
      setDriverLastName(option.gateIn.driver?.lastName || '');
      setPhoneNumber(option.gateIn.driver?.phoneNumber);

      setZoomImageIndex(0);
      setImages([]);
      setImagesFiles([]);

      setFiles([]);

      filesInputRef.current.type = 'text';
      filesInputRef.current.type = 'file';
      filesGalleryInputRef.current.type = 'text';
      filesGalleryInputRef.current.type = 'file';
      imagesInputRef.current.type = 'text';
      imagesInputRef.current.type = 'file';
      imagesGalleryInputRef.current.type = 'text';
      imagesGalleryInputRef.current.type = 'file';
      setUpdateKey(Math.random());
    }
  };

  const handleDeleteImageClick = (i: number) => {
    setImages((draft) => [...draft.filter((_: any, index: number) => index !== i)]);
    setImagesFiles((draft) => [...draft.filter((_: any, index: number) => index !== i)]);
  };

  const handleDeleteFileClick = (i: number) => {
    setFiles((draft) => [...draft.filter((_: any, index: number) => index !== i)]);
  };

  const handleZoomImageClick = (i: number) => {
    setZoomImageIndex(i);
    fullScreenButtonClick();
  };

  const handleChangeImages = (
    newImages: any,
    newFiles: any,
    isCapture?: boolean,
    isOcr?: boolean,
    isSaveImage = true,
  ) => {
    if (filesFormatValidator(newFiles, 'Image')) {
      enqueueSnackbar(filesFormatValidator(newFiles, 'Image'), 'warning');
      return;
    }

    if (isCapture) {
      downloadBlob(newFiles[0], newFiles[0].name);
      if (isOcr) {
        getContainerOcr(newFiles[0], {
          onSuccess: ({ data }: any) => {
            if (data.car) {
              setLicensePlateNumber(data.car);
              return;
            }

            setContainerNumber(data.number);
            setContainerType(data.type);

            if (!data.number) return;
            gateInsService
              .options({
                status: 'ACTIVE',
                containerNumber: data.number,
                notLoading: false,
                take: 1,
              })()
              .then((options) => {
                if (options[0].id === 'fakeID' || !options[0].id) return;

                handleContainerSelect(options[0]);
              });
          },
        });
      }
    }

    if (isSaveImage) {
      setImages((draft) => [...draft, ...newImages]);
      setImagesFiles((draft) => [...draft, ...newFiles]);
    }

    ocrImageInputRef.current.type = 'text';
    ocrImageInputRef.current.type = 'file';
    ocrCarImageInputRef.current.type = 'text';
    ocrCarImageInputRef.current.type = 'file';
    imagesInputRef.current.type = 'text';
    imagesInputRef.current.type = 'file';
    imagesGalleryInputRef.current.type = 'text';
    imagesGalleryInputRef.current.type = 'file';
  };

  const handleChangeFiles = (newImages: any, newFiles: any, isCapture = false) => {
    if (filesFormatValidator(newFiles, 'Image')) {
      enqueueSnackbar(filesFormatValidator(newFiles, 'Image'), 'warning');
      return;
    }

    if (isCapture) {
      downloadBlob(newFiles[0], newFiles[0].name);
    }

    setFiles((draft) => [...draft, ...newFiles]);

    filesInputRef.current.type = 'text';
    filesInputRef.current.type = 'file';
    filesGalleryInputRef.current.type = 'text';
    filesGalleryInputRef.current.type = 'file';
  };

  const handleSubmitClick = () => {
    setIsSubmitClicked(true);

    if (Object.values(validators).some((field) => !!field)) {
      return;
    }

    const containerUploadDocumentsMutate = () =>
      updateDocumentsMutation.mutateAsync({
        id: containerId.current,
        documents: files.map((file) => ({
          action: 'upload',
          file,
        })),
      });

    const containerUploadImageMutate = () =>
      updateImagesMutation.mutateAsync({
        id: containerId.current,
        photos: imagesFiles.map((file) => ({
          action: 'upload',
          file,
        })),
      });

    const gateinCompleteMutate = () => {
      const data: any = {
        driver: {
          firstName: driverFirstName,
          lastName: driverLastName,
          licensePlateNumber,
          phoneNumber: getPhoneNumberValue(phoneNumber),
        },
        containerType,
        condition,
      };

      Object.keys(data.driver).forEach((key) => {
        if (!data.driver[key]) delete data.driver[key];
      });
      if (!Object.keys(data.driver).length) delete data.driver;

      completeMutation.mutate(
        {
          id: containerId.current,
          props: data,
        },
        {
          onSuccess: async () => {
            await containerUploadImageMutate();
            await containerUploadDocumentsMutate();

            controlBackdropLoading(-1);
            enqueueSnackbar(MESSAGES.CONTAINERS.SUCCESS.CREATE, 'success');
            resetForm();
          },
          onError: () => enqueueSnackbar(MESSAGES.GATE_IN.ERROR.NOT_COMPLETED, 'error'),
        },
      );
    };

    const gateinAddMutate = () =>
      gateinAddMutation.mutate(
        [
          {
            yardId: yardId.current,
            clientId: clientId.current,
            validFrom: moment(),
            validTo: moment(),
            transport: 'ground',
            releaseNumber: '',
            comment: '',

            containerNumber,
            containerType,
            condition,
            isLoaded: containerContent === 'loaded',
          },
        ],
        {
          onSuccess: (res: any) => {
            containerId.current = res.id;
            gateinCompleteMutate();
          },
          onError: (err: any) => {
            controlBackdropLoading(-1);
            if (err.statusCode === 409) {
              enqueueSnackbar(MESSAGES.CONTAINERS.ERROR.DUPLICATED, 'error');
            } else {
              enqueueSnackbar(MESSAGES.GATE_IN.ERROR.NOT_ADDED, 'error');
            }
          },
        },
      );

    controlBackdropLoading(1);
    if (containerId.current) {
      gateinCompleteMutate();
    } else {
      gateinAddMutate();
    }
  };

  return (
    <>
      <DashboardWrapper>
        <Typography variant={'textXL'} weight={'bold'}>
          Gate In With Image
        </Typography>
        <DashboardInputs key={updateKey}>
          <DashboardInputs
            columns={process.env.REACT_APP_TEZPORT_API_URL ? '1fr max-content' : '1fr'}
          >
            <SearchableSelect
              InputComponent={InputContainer}
              label={'Container Number*'}
              selectedOptionLabel={containerNumber || 'Select Container Number'}
              isLoading={isOcrLoading}
              onSelect={handleContainerSelect}
              queryKey={'gate-ins'}
              inputValueKey={(inputValue: string) =>
                extendContainerValidator(inputValue) && inputValue.length >= 3 ? inputValue : ''
              }
              queryFn={(inputValue: string) =>
                gateInsService.options({
                  status: 'ACTIVE',
                  ...(extendContainerValidator(inputValue) && inputValue.length >= 3
                    ? { containerNumber: inputValue }
                    : {}),
                })
              }
              selectedAreasEqaul={{ key: 'id', keys: [containerId.current] }}
              error={isSubmitClicked && !!validators.containerNumber}
              helperText={isSubmitClicked ? validators.containerNumber : ''}
            />
            <Button
              className={'row-button-square'}
              onClick={() => ocrImageInputRef.current.click()}
              isSquare
            >
              <DocumentScannerIcon />
            </Button>
          </DashboardInputs>
          <Select
            label={'Container Type*'}
            placeholder={'Select Container Type'}
            isLoading={isOcrLoading}
            options={containerTypes}
            value={containerType}
            onSelect={setContainerType}
            error={isSubmitClicked && !!validators.containerType}
            helperText={isSubmitClicked ? validators.containerType : ''}
          />
          <Select
            label={'Condition'}
            placeholder={'Select Condition'}
            options={containerConditionOptions}
            value={condition}
            onSelect={setCondition}
          />
          {!containerId.current && (
            <Select
              label={'Container Content*'}
              placeholder={'Select Container Content'}
              options={containerContents}
              value={containerContent}
              onSelect={setContainerContent}
              error={isSubmitClicked && !!validators.containerContent}
              helperText={isSubmitClicked ? validators.containerContent : ''}
            />
          )}
          {!containerId.current && (
            <BelongsToSelects
              handleYardCompanyChange={(obj) => {
                yardCompanyId.current = obj.id;
              }}
              handleYardChange={(obj) => {
                yardId.current = obj.id;
              }}
              handleClientChange={(obj) => {
                clientId.current = obj.id;
                setClientName(obj.label);
              }}
              isSubmitClicked={isSubmitClicked}
              yardName={containerId.current ? 'Default Yard' : undefined}
              clientName={clientName}
              hidePlaces
            />
          )}
          {containerId.current && clientName && (
            <Input
              label={'Client*'}
              placeholder={'Select Client'}
              data-size={'medium'}
              defaultValue={clientName}
              disabled
            />
          )}
          <DashboardInputs
            columns={process.env.REACT_APP_TEZPORT_API_URL ? '1fr max-content' : '1fr'}
          >
            <Input
              label={'License Plate Number*'}
              placeholder={'License Plate Number'}
              data-size={'medium'}
              value={licensePlateNumber}
              onChange={(e) => setLicensePlateNumber(e.target.value)}
              error={isSubmitClicked && !!validators.licensePlateNumber}
              helperText={isSubmitClicked ? validators.licensePlateNumber : ''}
            />
            <Button
              className={'row-button-square'}
              onClick={() => ocrCarImageInputRef.current.click()}
              isSquare
            >
              <DocumentScannerIcon />
            </Button>
          </DashboardInputs>
          <Input
            label={'Driver First Name'}
            placeholder={'Driver First Name'}
            data-size={'medium'}
            defaultValue={driverFirstName}
            onChange={(e) => setDriverFirstName(e.target.value)}
            error={isSubmitClicked && !!validators.driverFirstName}
            helperText={isSubmitClicked ? validators.driverFirstName : ''}
          />
          <Input
            label={'Driver Last Name'}
            placeholder={'Driver Last Name'}
            data-size={'medium'}
            defaultValue={driverLastName}
            onChange={(e) => setDriverLastName(e.target.value)}
            error={isSubmitClicked && !!validators.driverLastName}
            helperText={isSubmitClicked ? validators.driverLastName : ''}
          />
          <InputPhoneNumber
            label={'Driver Phone Number'}
            placeholder={'Driver Phone Number'}
            data-size={'medium'}
            value={phoneNumber}
            defaultValue={'+998'}
            defaultCountry={'UZ'}
            onChange={(e) => setPhoneNumber(e.target.value)}
            error={isSubmitClicked && !!validators.phoneNumber}
            helperText={isSubmitClicked ? validators.phoneNumber : ''}
          />
          <DashboardInputs>
            <DashboardInputs columns={'1fr max-content max-content'}>
              <Typography
                variant={'textM'}
                weight={'bold'}
                color={isSubmitClicked && validators.images ? '--red-100' : '--gray-100'}
              >
                Images*
              </Typography>
              <Button onClick={() => imagesInputRef.current.click()} isSquare>
                <AddAPhotoIcon />
              </Button>
              <Button onClick={() => imagesGalleryInputRef.current.click()} isSquare>
                <CollectionsIcon />
              </Button>
            </DashboardInputs>
            <HideInput>
              <Input
                id={'ocr-image'}
                label={'OCR Image'}
                data-size={'medium'}
                type={'file'}
                inputRef={ocrImageInputRef}
                inputProps={{
                  accept: '.jpeg, .jpg, .png, .webp',
                  capture: 'environment',
                }}
                value={undefined}
                handleChangeFiles={(newImages, newFiles) =>
                  handleChangeImages(newImages, newFiles, true, true)
                }
              />
              <Input
                id={'ocr-car-image'}
                label={'OCR Car Image'}
                data-size={'medium'}
                type={'file'}
                inputRef={ocrCarImageInputRef}
                inputProps={{
                  accept: '.jpeg, .jpg, .png, .webp',
                  capture: 'environment',
                }}
                value={undefined}
                handleChangeFiles={(newImages, newFiles) =>
                  handleChangeImages(newImages, newFiles, true, true, false)
                }
              />
              <Input
                id={'images'}
                label={'Container Images'}
                data-size={'medium'}
                type={'file'}
                inputRef={imagesInputRef}
                inputProps={{
                  accept: '.jpeg, .jpg, .png, .webp',
                  multiple: true,
                  capture: 'environment',
                }}
                value={undefined}
                handleChangeFiles={(newImages, newFiles) =>
                  handleChangeImages(newImages, newFiles, true)
                }
              />
              <Input
                id={'images'}
                label={'Container Images'}
                data-size={'medium'}
                type={'file'}
                inputRef={imagesGalleryInputRef}
                inputProps={{
                  accept: '.jpeg, .jpg, .png, .webp',
                  multiple: true,
                }}
                value={undefined}
                handleChangeFiles={handleChangeImages}
              />
            </HideInput>
            {!!images.length && (
              <ImageGalleryCardWrapper>
                {images.map((img: any, i: any) => (
                  <ImageGalleryCard
                    key={i}
                    img={img.url || img}
                    handleZoomClick={() => handleZoomImageClick(i)}
                    handleDeleteImageClick={() => handleDeleteImageClick(i)}
                  />
                ))}
              </ImageGalleryCardWrapper>
            )}
          </DashboardInputs>
          <DashboardInputs>
            <DashboardInputs columns={'1fr max-content max-content'}>
              <Typography
                variant={'textM'}
                weight={'bold'}
                color={isSubmitClicked && validators.files ? '--red-100' : '--gray-100'}
              >
                Documents
              </Typography>
              <Button onClick={() => filesInputRef.current.click()} isSquare>
                <AddAPhotoIcon />
              </Button>
              <Button onClick={() => filesGalleryInputRef.current.click()} isSquare>
                <InsertDriveFileIcon />
              </Button>
            </DashboardInputs>
            <HideInput>
              <Input
                id={'documents'}
                label={'Container Documents'}
                data-size={'medium'}
                type={'file'}
                inputRef={filesInputRef}
                inputProps={{
                  accept: '.jpeg, .jpg, .png, .webp',
                  multiple: true,
                  capture: 'environment',
                }}
                value={undefined}
                handleChangeFiles={(newImages, newFiles) =>
                  handleChangeFiles(newImages, newFiles, true)
                }
                withoutCompress
              />
              <Input
                id={'documents-gallery'}
                label={'Container Documents'}
                data-size={'medium'}
                type={'file'}
                inputRef={filesGalleryInputRef}
                inputProps={{
                  accept: '*',
                  multiple: true,
                }}
                value={undefined}
                handleChangeFiles={handleChangeFiles}
                withoutCompress
              />
            </HideInput>
            {!!files.length &&
              files.map((file: any, i: any) => (
                <FileRow
                  key={i}
                  file={file}
                  handleDeleteFileClick={() => handleDeleteFileClick(i)}
                  variant="input"
                  withoutBorder
                />
              ))}
          </DashboardInputs>
        </DashboardInputs>
        <DashbaordFormFooter>
          <Button size={'medium'} onClick={handleSubmitClick}>
            Submit
          </Button>
        </DashbaordFormFooter>
      </DashboardWrapper>
      <ImageGallery photos={images} startIndex={zoomImageIndex} />
    </>
  );
};
